import React, { useEffect, useState, useCallback } from 'react';
import { useCompany } from '../contexts/CompanyContext';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase_client';
import '../styles/page-base.css';

interface Group {
  id: string;
  name: string;
  description?: string;
  member_count?: number;
}

interface GroupsProps {
  onEditingChange?: (isEditing: boolean, isAdding: boolean, groupId?: string | null) => void;
  onSave?: (groupId?: string) => Promise<void>;
  onCancel?: () => void;
  editingGroupId?: string | null;
  isAddingGroup?: boolean;
  editingGroupName?: string;
  editingGroupDescription?: string;
  onEditingGroupNameChange?: (name: string) => void;
  onEditingGroupDescriptionChange?: (description: string) => void;
  onRefreshRequest?: (refreshFn: () => Promise<void>) => void;
}

export default function Groups({
  onEditingChange,
  onSave,
  onCancel,
  editingGroupId: externalEditingGroupId,
  isAddingGroup: externalIsAddingGroup,
  editingGroupName: externalEditingGroupName,
  editingGroupDescription: externalEditingGroupDescription,
  onEditingGroupNameChange,
  onEditingGroupDescriptionChange,
  onRefreshRequest
}: GroupsProps = {}) {
  const { selectedCompany } = useCompany();
  const { user, isUserAdmin } = useSession();
  const [groups, setGroups] = useState<Group[]>([]);
  const [showDeleteGroupConfirm, setShowDeleteGroupConfirm] = useState<string | null>(null);
  // Use external state if provided, otherwise use internal state
  const [internalEditingGroupId, setInternalEditingGroupId] = useState<string | null>(null);
  const [internalIsAddingGroup, setInternalIsAddingGroup] = useState(false);
  const [internalEditingGroupName, setInternalEditingGroupName] = useState('');
  const [internalEditingGroupDescription, setInternalEditingGroupDescription] = useState('');
  
  const editingGroupId = externalEditingGroupId !== undefined ? externalEditingGroupId : internalEditingGroupId;
  const isAddingGroup = externalIsAddingGroup !== undefined ? externalIsAddingGroup : internalIsAddingGroup;
  const editingGroupName = externalEditingGroupName !== undefined ? externalEditingGroupName : internalEditingGroupName;
  const editingGroupDescription = externalEditingGroupDescription !== undefined ? externalEditingGroupDescription : internalEditingGroupDescription;
  
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [currentUserGroupIds, setCurrentUserGroupIds] = useState<string[]>([]);

  const fetchCurrentUserGroups = useCallback(async () => {
    if (!selectedCompany || !user?.id) {
      setCurrentUserGroupIds([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id)
        .eq('org_id', selectedCompany.company_id);

      if (error) {
        console.error('Error fetching current user groups:', error);
        return;
      }

      const groupIds = data?.map(gm => gm.group_id) || [];
      setCurrentUserGroupIds(groupIds);
    } catch (error) {
      console.error('Error fetching current user groups:', error);
    }
  }, [selectedCompany?.company_id, user?.id]);

  useEffect(() => {
    fetchCurrentUserGroups();
  }, [selectedCompany?.company_id, user?.id, fetchCurrentUserGroups]);

  const fetchGroups = useCallback(async () => {
    if (!selectedCompany) {
      setGroups([]);
      setGroupsLoading(false);
      return;
    }
    
    try {
      setGroupsLoading(true);
      
      const { data, error } = await supabase
        .from('organization_groups')
        .select('id, name, description')
        .eq('org_id', selectedCompany.company_id)
        .order('name');
      
      if (error) {
        console.error('Error fetching groups:', error);
        setGroupsLoading(false);
        return;
      }
      
      // For admins, show all groups. For non-admins, only show groups they belong to
      let filteredGroups = data || [];
      if (!isUserAdmin && currentUserGroupIds.length > 0) {
        filteredGroups = filteredGroups.filter(g => currentUserGroupIds.includes(g.id));
      } else if (!isUserAdmin) {
        setGroups([]);
        setGroupsLoading(false);
        return;
      }
      
      // Count members per group from group_members table
      const groupsWithCounts = await Promise.all(
        filteredGroups.map(async (group) => {
          const { count } = await supabase
            .from('group_members')
            .select('*', { count: 'exact', head: true })
            .eq('group_id', group.id)
            .eq('org_id', selectedCompany.company_id);
          
          return {
            ...group,
            member_count: count || 0
          };
        })
      );
      
      setGroups(groupsWithCounts);
    } catch (error) {
      console.error('Error fetching groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  }, [selectedCompany?.company_id, currentUserGroupIds, isUserAdmin]);

  useEffect(() => {
    if (selectedCompany) {
      fetchGroups();
    }
  }, [selectedCompany?.company_id, currentUserGroupIds, isUserAdmin, fetchGroups]);
  
  useEffect(() => {
    if (onRefreshRequest) {
      onRefreshRequest(fetchGroups);
    }
  }, [onRefreshRequest, fetchGroups]);
  
  // Update handleStartEdit to notify parent
  const handleStartEdit = (group: Group) => {
    const newName = group.name;
    const newDescription = group.description || '';
    
    if (onEditingGroupNameChange) {
      onEditingGroupNameChange(newName);
    } else {
      setInternalEditingGroupName(newName);
    }
    
    if (onEditingGroupDescriptionChange) {
      onEditingGroupDescriptionChange(newDescription);
    } else {
      setInternalEditingGroupDescription(newDescription);
    }
    
    if (onEditingChange) {
      onEditingChange(true, false, group.id); // ✅ Pass the group ID
    } else {
      setInternalEditingGroupId(group.id);
    }
  };

  const handleStartAdd = () => {
    if (onEditingGroupNameChange) {
      onEditingGroupNameChange('');
    } else {
      setInternalEditingGroupName('');
    }
    
    if (onEditingGroupDescriptionChange) {
      onEditingGroupDescriptionChange('');
    } else {
      setInternalEditingGroupDescription('');
    }
    
    if (onEditingChange) {
      onEditingChange(false, true, null); // ✅ Pass null for adding
    } else {
      setInternalIsAddingGroup(true);
    }
  };

  const handleDeleteGroup = async (groupId: string) => {
    setShowDeleteGroupConfirm(groupId);
  };

  const confirmDeleteGroup = async (groupId: string) => {
    try {
      // First remove all members from this group
      await supabase
        .from('group_members')
        .delete()
        .eq('group_id', groupId);

      // Then delete the group
      const { error } = await supabase
        .from('organization_groups')
        .delete()
        .eq('id', groupId)
        .eq('org_id', selectedCompany?.company_id || '');

      if (error) throw error;

      fetchGroups();
      setShowDeleteGroupConfirm(null);
    } catch (error: any) {
      console.error('Error deleting group:', error);
      alert('Failed to delete group: ' + (error.message || 'Unknown error'));
      setShowDeleteGroupConfirm(null);
    }
  };

  return (
    <div>
      {/* Remove the page-card and header - just show groups grid */}
      {groupsLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center space-x-2 text-gray-500">
            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm">Loading groups...</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {/* Existing Groups */}
          {groups.map((group) => {
            const isEditing = editingGroupId === group.id;
            
            return (
              <div key={group.id} className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow relative group h-32 flex flex-col">
                {isEditing ? (
                  // Editable mode - no buttons, they're in the header
                  <div className="flex flex-col h-full">
                    <div className="flex-1 space-y-3">
                      <div>
                        <input
                          type="text"
                          value={editingGroupName}
                          onChange={(e) => {
                            if (onEditingGroupNameChange) {
                              onEditingGroupNameChange(e.target.value);
                            } else {
                              setInternalEditingGroupName(e.target.value);
                            }
                          }}
                          className="w-full border-2 border-blue-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Group name"
                          autoFocus
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          value={editingGroupDescription}
                          onChange={(e) => {
                            if (onEditingGroupDescriptionChange) {
                              onEditingGroupDescriptionChange(e.target.value);
                            } else {
                              setInternalEditingGroupDescription(e.target.value);
                            }
                          }}
                          className="w-full border-2 border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Description (optional)"
                        />
                      </div>
                      {errorMsg && editingGroupId === group.id && (
                        <p className="text-xs text-red-600">{errorMsg}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  // View mode
                  <>
                    <div className="flex items-start justify-between mb-2 flex-1">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-gray-900 truncate">{group.name}</h3>
                        {group.description && (
                          <p className="text-sm text-gray-500 mt-1 line-clamp-2">{group.description}</p>
                        )}
                      </div>
                      {isUserAdmin && (
                        <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity ml-2">
                          <button
                            onClick={() => handleStartEdit(group)}
                            className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                            title="Edit group"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteGroup(group.id)}
                            className="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition"
                            title="Delete group"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="mt-auto pt-3 border-t border-gray-100">
                      <div className="flex items-center space-x-1.5 text-gray-600">
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="text-sm font-medium">{group.member_count || 0}</span>
                        <span className="text-xs text-gray-500">{group.member_count === 1 ? 'member' : 'members'}</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })}
          
          {/* Add Group Card - Only show for admins */}
          {isUserAdmin && (
            <div className="bg-white rounded-lg border-2 border-dashed border-gray-300 p-4 h-32 flex flex-col">
              {isAddingGroup ? (
                // Editable mode for new group - no buttons
                <div className="flex flex-col h-full">
                  <div className="flex-1 space-y-3">
                    <div>
                      <input
                        type="text"
                        value={editingGroupName}
                        onChange={(e) => {
                          if (onEditingGroupNameChange) {
                            onEditingGroupNameChange(e.target.value);
                          } else {
                            setInternalEditingGroupName(e.target.value);
                          }
                        }}
                        className="w-full border-2 border-blue-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Group name"
                        autoFocus
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        value={editingGroupDescription}
                        onChange={(e) => {
                          if (onEditingGroupDescriptionChange) {
                            onEditingGroupDescriptionChange(e.target.value);
                          } else {
                            setInternalEditingGroupDescription(e.target.value);
                          }
                        }}
                        className="w-full border-2 border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Description (optional)"
                      />
                    </div>
                    {errorMsg && isAddingGroup && (
                      <p className="text-xs text-red-600">{errorMsg}</p>
                    )}
                  </div>
                </div>
              ) : (
                // View mode - clickable add button
                <button
                  onClick={handleStartAdd}
                  className="h-full flex flex-col items-center justify-center group hover:border-blue-400 hover:bg-blue-50 transition-all"
                >
                  <div className="flex flex-col items-center justify-center">
                    <div className="p-2 bg-blue-100 rounded-full mb-2 group-hover:bg-blue-200 transition-colors">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">
                      Add Group
                    </span>
                  </div>
                </button>
              )}
            </div>
          )}
        </div>
      )}
      {/* Delete Group Confirmation Modal */}
      {showDeleteGroupConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Group</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this group? Members will be unassigned from this group.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowDeleteGroupConfirm(null)}
                className="f-btn f-btn-soft"
              >
                Cancel
              </button>
              <button
                onClick={() => confirmDeleteGroup(showDeleteGroupConfirm)}
                className="f-btn f-btn-danger"
              >
                Delete Group
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}